import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import MuiTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import LinearProgress from '@material-ui/core/LinearProgress';
import TableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';

import { SortOrder } from 'enums';

import { useStyles } from './table-styles';
import { TableProps } from './table-types';
import { EnhancedTableHead } from './table-header';
import { TableCell } from './table-cell';
import { EnhancedTableRow } from './table-row';

export function Table<TObject extends { id: string }>(
  props: TableProps<TObject>
) {
  const {
    columns,
    rows = [],
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    count,
    setCount,
    searching,
    Footer,
    classes: propsClasses = {},
    expandableRows = false,
    AdditionalRowComponent,
    expanded: propsExpanded,
    setExpanded: setPropsExpanded,
    handleChangePage: defaultHandleChangePage,
    handleChangeRowsPerPage: defaultHandleChangeRowsPerPage,
  } = props;

  const defaultClasses = useStyles();
  const classes = { ...defaultClasses, ...propsClasses };
  const history = useHistory();
  const [defaultExpanded, setDefaultExpanded] = useState<string[]>([]);

  const expanded = propsExpanded ?? defaultExpanded;
  const setExpanded =
    setPropsExpanded ??
    ((id: string) => {
      if (defaultExpanded.some((de) => de === id)) {
        setDefaultExpanded(defaultExpanded.filter((de) => de !== id));
      } else {
        setDefaultExpanded([...defaultExpanded, id]);
      }
    });

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === SortOrder.ASC;
    setOrder(isAsc ? SortOrder.DESC : SortOrder.ASC);
    setOrderBy(property);
    setPage(0);
    setCount(0);
  };

  const handleChangePage =
    defaultHandleChangePage ??
    ((event: unknown, newPage: number) => {
      setPage(newPage);
    });

  const handleChangeRowsPerPage =
    defaultHandleChangeRowsPerPage ??
    ((event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    });

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.tableContainer}>
          <MuiTable
            className={classes.table}
            aria-labelledby="tableTitle"
            size={'medium'}
            aria-label="enhanced table"
            stickyHeader
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={0}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              columns={columns}
              expandableRows={expandableRows}
            />
            {!searching && rows.length === 0 && (
              <TableRow>
                <MuiTableCell colSpan={12}>Nic nenalezeno</MuiTableCell>
              </TableRow>
            )}

            <TableBody>
              {!searching &&
                rows.map((row) => {
                  return (
                    <EnhancedTableRow<TObject>
                      key={row.id}
                      row={row}
                      expandable={expandableRows}
                      AdditionalComponent={AdditionalRowComponent}
                      expanded={expanded}
                      setExpanded={setExpanded}
                    >
                      {columns.map((c) => (
                        <TableCell
                          key={c.id}
                          row={row}
                          column={c}
                          history={history}
                        />
                      ))}
                    </EnhancedTableRow>
                  );
                })}
            </TableBody>
          </MuiTable>
        </TableContainer>
        {searching && <LinearProgress />}

        {Footer && <Footer />}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Řádků na stránku:"
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
